import { formatDate, formatDateTime } from '../utils/dates.ts'
import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import {
  ArrowDownTrayIcon,
  CheckCircleIcon,
  XCircleIcon,
  Cog6ToothIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { api } from '../services/api.ts'
import LoadingSpinner from '../components/LoadingSpinner.tsx'
import PasswordInput from '../components/PasswordInput.tsx'
import ConfirmDialog from '../components/ConfirmDialog.tsx'

export default function Export() {
  const queryClient = useQueryClient()
  const [selectedJobIds, setSelectedJobIds] = useState<string[]>([])
  const [includeDuplicates, setIncludeDuplicates] = useState(false)
  const [showZohoConfig, setShowZohoConfig] = useState(false)
  const [zohoConfig, setZohoConfig] = useState({
    clientId: '',
    clientSecret: '',
    redirectUri: '',
  })
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const [selectedLeadFile, setSelectedLeadFile] = useState<File | null>(null)
  const fileLeadInputRef = React.useRef<HTMLInputElement>(null)
  const [tokenValidationStatus, setTokenValidationStatus] = useState<{
    isValid: boolean | null
    message: string
    isLoading: boolean
  }>({
    isValid: null,
    message: '',
    isLoading: false,
  })
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [exportJobToDelete, setExportJobToDelete] = useState<string | null>(
    null
  )
  const [stateFilter, setStateFilter] = useState('')

  // Fetch completed jobs for selection
  const { data: jobs, isLoading: jobsLoading } = useQuery(
    'completed-jobs',
    () => api.get('/api/jobs/?status=completed')
  )

  // Fetch Zoho connection status
  const { data: zohoStatus, isLoading: zohoStatusLoading } = useQuery(
    'zoho-status',
    () => api.get('/api/export/zoho/status')
  )

  // Fetch Zoho accounts status
  const { data: accountsStatus } = useQuery('zoho-accounts-status', () =>
    api.get('/api/export/zoho/accounts/status')
  )

  // Fetch Zoho leads status
  const { data: leadsStatus } = useQuery('zoho-leads-status', () =>
    api.get('/api/export/zoho/leads/status')
  )

  // Fetch export history
  const { data: exportHistory, isLoading: exportHistoryLoading } = useQuery(
    'export-history',
    () => api.get('/api/export/history?limit=20')
  )

  // Export mutations
  const exportCSVMutation = useMutation(
    () =>
      api.post(
        '/api/export/csv',
        {
          job_ids: selectedJobIds,
          format: 'csv',
          include_duplicates: includeDuplicates,
        },
        { responseType: 'blob' }
      ),
    {
      onSuccess: (response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'stores_export.csv')
        document.body.appendChild(link)
        link.click()
        link.remove()
        toast.success('CSV downloaded successfully')
        queryClient.invalidateQueries('export-history')
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'Failed to export CSV')
      },
    }
  )

  const exportExcelMutation = useMutation(
    () =>
      api.post(
        '/api/export/excel',
        {
          job_ids: selectedJobIds,
          format: 'xlsx',
          include_duplicates: includeDuplicates,
        },
        { responseType: 'blob' }
      ),
    {
      onSuccess: (response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'stores_export.xlsx')
        document.body.appendChild(link)
        link.click()
        link.remove()
        toast.success('Excel file downloaded successfully')
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'Failed to export Excel')
      },
    }
  )

  const saveZohoConfigMutation = useMutation(
    (config: typeof zohoConfig) => api.post('/api/export/zoho/config', config),
    {
      onSuccess: () => {
        toast.success('Zoho configuration saved')
        queryClient.invalidateQueries('zoho-status')
        setShowZohoConfig(false)
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.detail || 'Failed to save Zoho configuration'
        )
      },
    }
  )

  const exportZohoMutation = useMutation(
    () =>
      api.post('/api/export/zoho', {
        job_ids: selectedJobIds,
        format: 'zoho',
        include_duplicates: includeDuplicates,
      }),
    {
      onSuccess: (response) => {
        const data = response.data

        if (data.success && data.leads_created > 0) {
          toast.success(
            data.message ||
              `Successfully exported ${data.leads_created} leads to Zoho CRM!`
          )
          if (data.duplicates_removed > 0) {
            toast(`${data.duplicates_removed} duplicate leads were skipped`, {
              icon: 'ℹ️',
            })
          }
          queryClient.invalidateQueries('export-history')
        } else {
          // Handle cases where no leads were created
          if (data.leads_created === 0) {
            if (data.duplicates_removed > 0) {
              toast(
                `No new leads exported. All ${data.total_stores} stores were duplicates and filtered out.`,
                {
                  icon: '⚠️',
                  style: {
                    background: '#fef3c7',
                    color: '#92400e',
                  },
                }
              )
            } else {
              toast(
                `No leads were exported. ${
                  data.message || 'Unknown issue occurred.'
                }`,
                {
                  icon: '⚠️',
                  style: {
                    background: '#fef3c7',
                    color: '#92400e',
                  },
                }
              )
            }
          } else {
            toast.error(data.message || 'Export failed')
          }
        }
      },
      onError: (error: any) => {
        console.error('Zoho export error:', error)
        let errorMessage = 'Failed to export to Zoho'

        if (error.response?.data?.detail) {
          if (typeof error.response.data.detail === 'string') {
            errorMessage = error.response.data.detail
          } else if (Array.isArray(error.response.data.detail)) {
            // Pydantic validation errors
            errorMessage = error.response.data.detail
              .map((err: any) => `${err.loc?.join('.') || 'Field'}: ${err.msg}`)
              .join(', ')
          }
        }

        toast.error(errorMessage)
      },
    }
  )

  // Where every row of a Zoho import went - a short import (e.g. 4,493 of
  // 9,394 accounts) used to show only bare counts in toasts that vanished
  const showImportReport = (data: any, kind: string) => {
    const reasons = data.skip_reasons || {}
    const cols = data.columns_used || {}
    const phoneCols = [cols.phone, cols.other_phone, cols.mobile]
      .filter(Boolean)
      .join(' → ')
    const lines = [
      `${data.total_records} rows → ${data.unique_phones ?? '?'} unique phone numbers`,
      `• ${data.records_imported} new ${kind}, ${data.duplicates_updated} already existed (updated)`,
    ]
    if (data.merged_in_file > 0) {
      lines.push(`• ${data.merged_in_file} rows shared a phone with another row (merged)`)
    }
    if (reasons.missing_phone > 0) {
      lines.push(`• ${reasons.missing_phone} skipped: no phone number (${phoneCols || 'no phone column found'})`)
    }
    if (reasons.missing_name > 0) {
      lines.push(`• ${reasons.missing_name} skipped: no name (column: ${cols.name || 'not found'})`)
    }
    if (reasons.error > 0) {
      lines.push(`• ${reasons.error} rows failed: ${(data.errors || [])[0] || ''}`)
    }
    if (data.phone_from_other_columns > 0) {
      lines.push(`• ${data.phone_from_other_columns} used Other Phone / Mobile`)
    }
    toast.success(lines.join('\n'), {
      duration: 20000,
      style: { whiteSpace: 'pre-line', maxWidth: 560 },
    })
  }

  // Upload errors from the proxy (e.g. 413) have no JSON `detail`, so explain them
  const uploadErrorMessage = (error: any, kind: string) => {
    const status = error.response?.status
    if (status === 413) {
      return `Upload rejected: the ${kind} file is too large for the server`
    }
    if (status === 504) {
      return `The ${kind} import timed out - check the counts before retrying`
    }
    return error.response?.data?.detail || `Failed to upload ${kind}`
  }

  // Upload Zoho accounts mutation
  const uploadAccountsMutation = useMutation(
    (formData: FormData) =>
      api.post('/api/export/zoho/accounts/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    {
      onSuccess: (response) => {
        showImportReport(response.data, 'accounts')
        queryClient.invalidateQueries('zoho-accounts-status')
        setSelectedFile(null)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      },
      onError: (error: any) => {
        toast.error(uploadErrorMessage(error, 'accounts'))
      },
    }
  )

  // Clear accounts mutation
  const clearAccountsMutation = useMutation(
    () => api.delete('/api/export/zoho/accounts'),
    {
      onSuccess: (response) => {
        toast.success(
          `Cleared ${response.data.deleted_count} accounts successfully`
        )
        queryClient.invalidateQueries('zoho-accounts-status')
      },
      onError: (error: any) => {
        toast.error('Failed to clear accounts')
      },
    }
  )

  const uploadLeadsMutation = useMutation(
    (formData: FormData) =>
      api.post('/api/export/zoho/leads/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    {
      onSuccess: (response) => {
        showImportReport(response.data, 'leads')
        queryClient.invalidateQueries('zoho-leads-status')
        setSelectedLeadFile(null)
        if (fileLeadInputRef.current) {
          fileLeadInputRef.current.value = ''
        }
      },
      onError: (error: any) => {
        toast.error(uploadErrorMessage(error, 'leads'))
      },
    }
  )

  const clearLeadsMutation = useMutation(
    () => api.delete('/api/export/zoho/leads'),
    {
      onSuccess: (response) => {
        toast.success(
          `Cleared ${response.data.deleted_count} leads successfully`
        )
        queryClient.invalidateQueries('zoho-leads-status')
      },
      onError: (error: any) => {
        toast.error('Failed to clear leads')
      },
    }
  )

  // Validate token mutation
  const validateTokenMutation = useMutation(
    () => api.post('/api/export/zoho/validate-token'),
    {
      onSuccess: (response) => {
        setTokenValidationStatus({
          isValid: response.data.valid,
          message: response.data.message,
          isLoading: false,
        })
        if (response.data.valid) {
          toast.success('Token is valid and working')
          queryClient.invalidateQueries('zoho-status')
        } else {
          toast.error(response.data.message)
        }
      },
      onError: (error: any) => {
        setTokenValidationStatus({
          isValid: false,
          message: error.response?.data?.detail || 'Token validation failed',
          isLoading: false,
        })
        toast.error(error.response?.data?.detail || 'Token validation failed')
      },
    }
  )

  // Refresh auth mutation
  const refreshAuthMutation = useMutation(
    () => api.post('/api/export/zoho/refresh-auth'),
    {
      onSuccess: (response) => {
        toast.success('Redirecting to Zoho for re-authentication...')
        window.location.href = response.data.auth_url
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.detail || 'Failed to refresh authentication'
        )
      },
    }
  )

  const handleZohoConnect = () => {
    // Redirect to Zoho OAuth
    window.location.href = 'http://localhost:8000/api/export/zoho/auth'
  }

  const handleValidateToken = () => {
    setTokenValidationStatus({
      isValid: null,
      message: '',
      isLoading: true,
    })
    validateTokenMutation.mutate()
  }

  const handleRefreshAuth = () => {
    refreshAuthMutation.mutate()
  }

  // Pre-populate form when Zoho config is loaded
  useEffect(() => {
    if (zohoStatus?.data?.config && showZohoConfig) {
      const config = zohoStatus.data.config
      setZohoConfig({
        clientId: config.client_id || '',
        clientSecret: config.client_secret || '',
        redirectUri: config.redirect_uri || '',
      })
    }
  }, [zohoStatus?.data?.config, showZohoConfig])

  // Delete export job mutation
  const deleteExportJobMutation = useMutation(
    (exportJobId: string) => api.delete(`/api/export/history/${exportJobId}`),
    {
      onSuccess: () => {
        toast.success('Failed export job deleted')
        queryClient.invalidateQueries('export-history')
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.detail || 'Failed to delete export job'
        )
      },
    }
  )

  // Handle delete export job
  const handleDeleteExportJob = (exportJobId: string) => {
    setExportJobToDelete(exportJobId)
    setShowDeleteConfirm(true)
  }

  const confirmDeleteExportJob = () => {
    if (exportJobToDelete) {
      deleteExportJobMutation.mutate(exportJobToDelete)
      setShowDeleteConfirm(false)
      setExportJobToDelete(null)
    }
  }

  const cancelDeleteExportJob = () => {
    setShowDeleteConfirm(false)
    setExportJobToDelete(null)
  }

  // Handle OAuth callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const zohoAuth = urlParams.get('zoho_auth')

    if (zohoAuth === 'success') {
      toast.success('Zoho authentication successful!')
      queryClient.invalidateQueries('zoho-status')
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname)
    } else if (zohoAuth === 'error') {
      const message = urlParams.get('message')
      toast.error(`Zoho authentication failed: ${message || 'Unknown error'}`)
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname)
    }
  }, [queryClient])

  const handleToggleJob = (jobId: string) => {
    setSelectedJobIds((prev) =>
      prev.includes(jobId)
        ? prev.filter((id) => id !== jobId)
        : [...prev, jobId]
    )
  }

  const handleSelectAll = () => {
    const allJobIds = jobs?.data?.map((job: any) => job.id) || []
    setSelectedJobIds(allJobIds)
  }

  const handleDeselectAll = () => {
    setSelectedJobIds([])
  }

  const handleSaveZohoConfig = (e: React.FormEvent) => {
    e.preventDefault()
    if (!zohoConfig.clientId || !zohoConfig.clientSecret) {
      toast.error('Please fill in all required fields')
      return
    }
    saveZohoConfigMutation.mutate(zohoConfig)
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
    }
  }

  const handleUploadAccounts = async () => {
    if (!selectedFile) {
      toast.error('Please select a file')
      return
    }

    const formData = new FormData()
    formData.append('file', selectedFile)

    uploadAccountsMutation.mutate(formData)
  }

  const handleClearAccounts = () => {
    const confirmToast = toast(
      (t) => (
        <div>
          <p className='font-medium mb-2'>Clear all imported accounts?</p>
          <p className='text-sm text-gray-600 mb-3'>
            You will need to re-import your accounts file.
          </p>
          <div className='flex space-x-2'>
            <button
              onClick={() => {
                clearAccountsMutation.mutate()
                toast.dismiss(t.id)
              }}
              className='px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700'
            >
              Clear
            </button>
            <button
              onClick={() => toast.dismiss(t.id)}
              className='px-3 py-1 bg-gray-300 rounded hover:bg-gray-400'
            >
              Cancel
            </button>
          </div>
        </div>
      ),
      { duration: 10000 }
    )
  }

  const handleLeadFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedLeadFile(file)
    }
  }

  const handleUploadLeads = async () => {
    if (!selectedLeadFile) {
      toast.error('Please select a file')
      return
    }

    const formData = new FormData()
    formData.append('file', selectedLeadFile)

    uploadLeadsMutation.mutate(formData)
  }

  const handleClearLeads = () => {
    const confirmToast = toast(
      (t) => (
        <div>
          <p className='font-medium mb-2'>Clear all imported leads?</p>
          <p className='text-sm text-gray-600 mb-3'>
            You will need to re-import your leads file.
          </p>
          <div className='flex space-x-2'>
            <button
              onClick={() => {
                clearLeadsMutation.mutate()
                toast.dismiss(t.id)
              }}
              className='px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700'
            >
              Clear
            </button>
            <button
              onClick={() => toast.dismiss(t.id)}
              className='px-3 py-1 bg-gray-300 rounded hover:bg-gray-400'
            >
              Cancel
            </button>
          </div>
        </div>
      ),
      { duration: 10000 }
    )
  }

  if (jobsLoading || zohoStatusLoading) {
    return <LoadingSpinner />
  }

  const completedJobs = jobs?.data || []
  const isZohoConnected = zohoStatus?.data?.connected || false
  const isZohoConfigured = zohoStatus?.data?.configured || false

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div>
        <h1 className='text-2xl font-bold text-gray-900 dark:text-white'>
          Export Data
        </h1>
        <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
          Export stores to CSV, Excel, or directly to Zoho CRM
        </p>
      </div>

      {/* Job Selection */}
      <div className='card'>
        <div className='card-header'>
          <h3 className='text-lg font-medium text-gray-900 dark:text-white'>
            Select Jobs
          </h3>
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
            Choose which completed jobs to export
          </p>
        </div>
        <div className='card-body'>
          {completedJobs.length === 0 ? (
            <div className='text-center py-8 bg-gray-50 rounded-lg'>
              <p className='text-sm text-gray-500'>
                No completed jobs available for export
              </p>
              <p className='text-xs text-gray-400 mt-1'>
                Complete a scraping job to export data
              </p>
            </div>
          ) : (
            <>
              <div className='flex items-center justify-between mb-4'>
                <span className='text-sm text-gray-700 dark:text-gray-300'>
                  {selectedJobIds.length} of {completedJobs.length} jobs
                  selected
                </span>
                <div className='space-x-2'>
                  <button
                    onClick={handleSelectAll}
                    className='text-sm text-blue-600 hover:text-blue-700'
                  >
                    Select All
                  </button>
                  <button
                    onClick={handleDeselectAll}
                    className='text-sm text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-400'
                  >
                    Deselect All
                  </button>
                </div>
              </div>

              <div className='space-y-2'>
                {completedJobs.map((job: any) => (
                  <div
                    key={job.id}
                    className='flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors dark:bg-gray-400 dark:hover:bg-gray-700'
                  >
                    <div className='flex items-center space-x-3'>
                      <input
                        type='checkbox'
                        checked={selectedJobIds.includes(job.id)}
                        onChange={() => handleToggleJob(job.id)}
                        className='rounded border-gray-300 text-blue-600 focus:ring-blue-500'
                      />
                      <div>
                        <p className='text-sm font-medium text-gray-900'>
                          {job.state} - {job.total_stores} stores
                        </p>
                        <p className='text-xs text-gray-500 dark:text-gray-800'>
                          {formatDate(job.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className='text-sm text-gray-600 dark:text-gray-800'>
                      {job.keywords.length} keywords
                    </div>
                  </div>
                ))}
              </div>

              {/* <div className='mt-4'>
                <label className='flex items-center space-x-2'>
                  <input
                    type='checkbox'
                    checked={includeDuplicates}
                    onChange={(e) => setIncludeDuplicates(e.target.checked)}
                    className='rounded border-gray-300 text-blue-600 focus:ring-blue-500'
                  />
                  <span className='text-sm text-gray-700 dark:text-gray-400'>
                    Include duplicate stores
                  </span>
                </label>
              </div> */}
            </>
          )}
        </div>
      </div>

      {/* Export Options */}
      <div className='card'>
        <div className='card-header'>
          <h3 className='text-lg font-medium text-gray-900 dark:text-gray-300'>
            Export Options
          </h3>
        </div>
        <div className='card-body'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            {/* CSV Export */}
            <button
              onClick={() => exportCSVMutation.mutate()}
              disabled={
                selectedJobIds.length === 0 || exportCSVMutation.isLoading
              }
              className='flex flex-col items-center justify-center p-6 border-2 border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-600 dark:hover:border-blue-500 dark:hover:bg-blue-900/20'
            >
              {exportCSVMutation.isLoading ? (
                <div className='flex flex-col items-center'>
                  <LoadingSpinner />
                  <h4 className='text-lg font-medium text-gray-900 dark:text-gray-200 mt-3'>
                    Generating CSV...
                  </h4>
                  <p className='text-sm text-gray-500 mt-1 text-center dark:text-gray-200'>
                    Preparing download...
                  </p>
                </div>
              ) : (
                <>
                  <ArrowDownTrayIcon className='h-12 w-12 text-gray-600 mb-3 dark:text-gray-200' />
                  <h4 className='text-lg font-medium text-gray-900 dark:text-gray-200'>
                    Export CSV
                  </h4>
                  <p className='text-sm text-gray-500 mt-1 text-center dark:text-gray-200'>
                    Download stores as CSV file
                  </p>
                </>
              )}
            </button>

            {/* Excel Export */}
            <button
              onClick={() => exportExcelMutation.mutate()}
              disabled={
                selectedJobIds.length === 0 || exportExcelMutation.isLoading
              }
              className='flex flex-col items-center justify-center p-6 border-2 border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-600 dark:hover:border-blue-500 dark:hover:bg-blue-900/20'
            >
              {exportExcelMutation.isLoading ? (
                <div className='flex flex-col items-center'>
                  <LoadingSpinner />
                  <h4 className='text-lg font-medium text-gray-900 dark:text-gray-200 mt-3'>
                    Generating Excel...
                  </h4>
                  <p className='text-sm text-gray-500 mt-1 text-center dark:text-gray-200'>
                    Preparing download...
                  </p>
                </div>
              ) : (
                <>
                  <ArrowDownTrayIcon className='h-12 w-12 text-gray-600 mb-3 dark:text-gray-200' />
                  <h4 className='text-lg font-medium text-gray-900 dark:text-gray-200'>
                    Export Excel
                  </h4>
                  <p className='text-sm text-gray-500 mt-1 text-center dark:text-gray-200'>
                    Download stores as XLSX file
                  </p>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Zoho CRM Integration */}
      <div className='card'>
        <div className='card-header'>
          <div className='flex items-center justify-between'>
            <div>
              <h3 className='text-lg font-medium text-gray-900 dark:text-gray-200'>
                Zoho CRM Integration
              </h3>
              <p className='mt-1 text-sm text-gray-500 dark:text-gray-200'>
                Export directly to Zoho CRM as leads
              </p>
            </div>
            <button
              onClick={() => setShowZohoConfig(!showZohoConfig)}
              className='text-gray-600 hover:text-gray-900'
            >
              <Cog6ToothIcon className='h-6 w-6 text-gray-600 dark:text-gray-200' />
            </button>
          </div>
        </div>
        <div className='card-body'>
          {/* Connection Status */}
          <div className='mb-6 p-4 rounded-lg bg-gray-50 dark:bg-gray-200'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center space-x-3'>
                {isZohoConnected ? (
                  <CheckCircleIcon className='h-6 w-6 text-green-600' />
                ) : (
                  <XCircleIcon className='h-6 w-6 text-red-600' />
                )}
                <div>
                  <p className='text-sm font-medium text-gray-900 dark:text-gray-700'>
                    Status:{' '}
                    {isZohoConnected ? (
                      <span className='text-green-600'>Connected</span>
                    ) : (
                      <span className='text-red-600'>Not Connected</span>
                    )}
                  </p>
                  {isZohoConnected && (
                    <p className='text-xs text-gray-500 mt-1 dark:text-gray-700'>
                      Ready to export leads to Zoho CRM
                    </p>
                  )}
                  {tokenValidationStatus.isValid !== null && (
                    <div className='mt-2 flex items-center space-x-2'>
                      {tokenValidationStatus.isValid ? (
                        <CheckCircleIcon className='h-4 w-4 text-green-600' />
                      ) : (
                        <ExclamationTriangleIcon className='h-4 w-4 text-red-600' />
                      )}
                      <p className='text-xs text-gray-600 dark:text-gray-600'>
                        {tokenValidationStatus.message}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className='flex items-center space-x-2'>
                {isZohoConfigured && (
                  <>
                    <button
                      onClick={handleValidateToken}
                      disabled={
                        validateTokenMutation.isLoading ||
                        tokenValidationStatus.isLoading
                      }
                      className='btn-secondary text-sm'
                    >
                      {validateTokenMutation.isLoading ||
                      tokenValidationStatus.isLoading ? (
                        <>
                          <ArrowPathIcon className='h-4 w-4 mr-1 animate-spin' />
                          Validating...
                        </>
                      ) : (
                        'Validate Token'
                      )}
                    </button>
                    <button
                      onClick={handleRefreshAuth}
                      disabled={refreshAuthMutation.isLoading}
                      className='btn-secondary text-sm'
                    >
                      {refreshAuthMutation.isLoading ? (
                        <>
                          <ArrowPathIcon className='h-4 w-4 mr-1 animate-spin' />
                          Refreshing...
                        </>
                      ) : (
                        'Refresh Auth'
                      )}
                    </button>
                  </>
                )}
                {isZohoConfigured && !isZohoConnected && (
                  <button onClick={handleZohoConnect} className='btn-primary'>
                    Connect to Zoho
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Configuration Form */}
          {showZohoConfig && (
            <div className='space-y-6'>
              <form onSubmit={handleSaveZohoConfig} className='space-y-4'>
                <div>
                  <label
                    htmlFor='clientId'
                    className='block text-sm font-medium text-gray-700 dark:text-gray-300'
                  >
                    Client ID *
                  </label>
                  <input
                    type='text'
                    id='clientId'
                    value={zohoConfig.clientId}
                    onChange={(e) =>
                      setZohoConfig({ ...zohoConfig, clientId: e.target.value })
                    }
                    className='input mt-1'
                    placeholder='Enter Zoho OAuth Client ID'
                  />
                </div>

                <PasswordInput
                  id='clientSecret'
                  label='Client Secret'
                  value={zohoConfig.clientSecret}
                  onChange={(value) =>
                    setZohoConfig({ ...zohoConfig, clientSecret: value })
                  }
                  placeholder='Enter Zoho OAuth Client Secret'
                  required={true}
                />

                <div>
                  <label
                    htmlFor='redirectUri'
                    className='block text-sm font-medium text-gray-700 dark:text-gray-300'
                  >
                    Redirect URI
                  </label>
                  <input
                    type='text'
                    id='redirectUri'
                    value={zohoConfig.redirectUri}
                    onChange={(e) =>
                      setZohoConfig({
                        ...zohoConfig,
                        redirectUri: e.target.value,
                      })
                    }
                    className='input mt-1'
                    placeholder='http://localhost:8000/api/export/zoho/callback'
                  />
                  <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                    Default: http://localhost:8000/api/export/zoho/callback
                  </p>
                </div>

                <div className='flex justify-end space-x-3 dark:text-gray-400'>
                  <button
                    type='button'
                    onClick={() => setShowZohoConfig(false)}
                    className='btn-secondary'
                  >
                    Cancel
                  </button>
                  <button
                    type='submit'
                    disabled={saveZohoConfigMutation.isLoading}
                    className='btn-primary'
                  >
                    {saveZohoConfigMutation.isLoading
                      ? 'Saving...'
                      : 'Save Configuration'}
                  </button>
                </div>
              </form>

              {/* NEW: Import Zoho Accounts Section */}
              <div className='border-t pt-6'>
                <h4 className='text-md font-medium text-gray-900 dark:text-white mb-2'>
                  Import Zoho Accounts
                </h4>
                <p className='text-sm text-gray-600 dark:text-gray-400 mb-4'>
                  Upload a CSV or Excel file of your existing Zoho accounts for
                  local de-duplication. This eliminates the need to fetch
                  accounts from Zoho API on every export.
                </p>

                {/* Upload Status */}
                {accountsStatus?.data?.has_accounts ? (
                  <div className='mb-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800'>
                    <div className='flex items-center justify-between'>
                      <div>
                        <p className='text-sm text-green-800 dark:text-green-300 font-medium'>
                          ✓ {accountsStatus.data.total_accounts} accounts
                          imported
                        </p>
                        {accountsStatus.data.last_upload_date && (
                          <p className='text-xs text-green-700 dark:text-green-400 mt-1'>
                            Last updated:{' '}
                            {formatDate(accountsStatus.data.last_upload_date)}
                          </p>
                        )}
                        {accountsStatus.data.last_upload_file && (
                          <p className='text-xs text-green-600 dark:text-green-400'>
                            File: {accountsStatus.data.last_upload_file}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={handleClearAccounts}
                        disabled={clearAccountsMutation.isLoading}
                        className='text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 font-medium disabled:opacity-50'
                      >
                        {clearAccountsMutation.isLoading
                          ? 'Clearing...'
                          : 'Clear & Re-import'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className='mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg'>
                    <p className='text-sm text-gray-600 dark:text-gray-400'>
                      No accounts imported yet
                    </p>
                  </div>
                )}

                {/* File Upload */}
                <div className='space-y-3'>
                  <div className='flex items-center space-x-3'>
                    <input
                      type='file'
                      accept='.csv,.xlsx,.xls'
                      onChange={handleFileSelect}
                      ref={fileInputRef}
                      className='hidden'
                    />
                    <button
                      type='button'
                      onClick={() => fileInputRef.current?.click()}
                      className='btn-secondary'
                    >
                      Choose File
                    </button>
                    {selectedFile && (
                      <span className='text-sm text-gray-700 dark:text-gray-300'>
                        {selectedFile.name}
                      </span>
                    )}
                  </div>

                  {selectedFile && (
                    <button
                      onClick={handleUploadAccounts}
                      disabled={uploadAccountsMutation.isLoading}
                      className='btn-primary inline-flex items-center'
                    >
                      {uploadAccountsMutation.isLoading && (
                        <svg
                          className='animate-spin -ml-1 mr-2 h-4 w-4 text-white'
                          xmlns='http://www.w3.org/2000/svg'
                          fill='none'
                          viewBox='0 0 24 24'
                        >
                          <circle
                            className='opacity-25'
                            cx='12'
                            cy='12'
                            r='10'
                            stroke='currentColor'
                            strokeWidth='4'
                          ></circle>
                          <path
                            className='opacity-75'
                            fill='currentColor'
                            d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                          ></path>
                        </svg>
                      )}
                      {uploadAccountsMutation.isLoading
                        ? 'Uploading'
                        : 'Upload Accounts'}
                    </button>
                  )}
                </div>

                {/* Expected Format Info */}
                <div className='mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800'>
                  <p className='text-xs font-medium text-blue-900 dark:text-blue-300 mb-2'>
                    Expected Format (column names from Zoho export):
                  </p>
                  <ul className='text-xs text-blue-800 dark:text-blue-300 space-y-1'>
                    <li>• Account Name (required)</li>
                    <li>• Phone (required for de-duplication)</li>
                    <li>• Account Number, Website, Billing info (optional)</li>
                  </ul>
                </div>
              </div>

              {/* Import Zoho Leads Section */}
              <div className='border-t pt-6 mt-6'>
                <h4 className='text-md font-medium text-gray-900 dark:text-white mb-2'>
                  Import Zoho Leads
                </h4>
                <p className='text-sm text-gray-600 dark:text-gray-400 mb-4'>
                  Upload a CSV or Excel file of your existing Zoho leads for
                  local de-duplication. This helps filter out stores that already exist as leads in Zoho.
                </p>

                {/* Upload Status */}
                {leadsStatus?.data?.has_leads ? (
                  <div className='mb-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800'>
                    <div className='flex items-center justify-between'>
                      <div>
                        <p className='text-sm text-green-800 dark:text-green-300 font-medium'>
                          ✓ {leadsStatus.data.total_leads} leads imported
                        </p>
                        {leadsStatus.data.last_upload_date && (
                          <p className='text-xs text-green-700 dark:text-green-400 mt-1'>
                            Last updated:{' '}
                            {formatDate(leadsStatus.data.last_upload_date)}
                          </p>
                        )}
                        {leadsStatus.data.last_upload_file && (
                          <p className='text-xs text-green-600 dark:text-green-400'>
                            File: {leadsStatus.data.last_upload_file}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={handleClearLeads}
                        disabled={clearLeadsMutation.isLoading}
                        className='text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 font-medium disabled:opacity-50'
                      >
                        {clearLeadsMutation.isLoading
                          ? 'Clearing...'
                          : 'Clear & Re-import'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className='mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg'>
                    <p className='text-sm text-gray-600 dark:text-gray-400'>
                      No leads imported yet
                    </p>
                  </div>
                )}

                {/* File Upload */}
                <div className='space-y-3'>
                  <div className='flex items-center space-x-3'>
                    <input
                      type='file'
                      accept='.csv,.xlsx,.xls'
                      onChange={handleLeadFileSelect}
                      ref={fileLeadInputRef}
                      className='hidden'
                    />
                    <button
                      type='button'
                      onClick={() => fileLeadInputRef.current?.click()}
                      className='btn-secondary'
                    >
                      Choose File
                    </button>
                    {selectedLeadFile && (
                      <span className='text-sm text-gray-700 dark:text-gray-300'>
                        {selectedLeadFile.name}
                      </span>
                    )}
                  </div>

                  {selectedLeadFile && (
                    <button
                      onClick={handleUploadLeads}
                      disabled={uploadLeadsMutation.isLoading}
                      className='btn-primary inline-flex items-center'
                    >
                      {uploadLeadsMutation.isLoading && (
                        <svg
                          className='animate-spin -ml-1 mr-2 h-4 w-4 text-white'
                          xmlns='http://www.w3.org/2000/svg'
                          fill='none'
                          viewBox='0 0 24 24'
                        >
                          <circle
                            className='opacity-25'
                            cx='12'
                            cy='12'
                            r='10'
                            stroke='currentColor'
                            strokeWidth='4'
                          ></circle>
                          <path
                            className='opacity-75'
                            fill='currentColor'
                            d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                          ></path>
                        </svg>
                      )}
                      {uploadLeadsMutation.isLoading
                        ? 'Uploading'
                        : 'Upload Leads'}
                    </button>
                  )}
                </div>

                {/* Expected Format Info */}
                <div className='mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800'>
                  <p className='text-xs font-medium text-blue-900 dark:text-blue-300 mb-2'>
                    Expected Format (column names from Zoho export):
                  </p>
                  <ul className='text-xs text-blue-800 dark:text-blue-300 space-y-1'>
                    <li>• Lead Name or Last Name (required)</li>
                    <li>• Phone (required for de-duplication)</li>
                    <li>• Company, Email, Lead Status, City, State (optional)</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Export to Zoho Button */}
          <div className='mt-6'>
            <button
              onClick={() => exportZohoMutation.mutate()}
              disabled={
                !isZohoConnected ||
                selectedJobIds.length === 0 ||
                exportZohoMutation.isLoading
              }
              className='w-full flex flex-col items-center justify-center p-6 border-2 border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-600 dark:hover:border-green-500 dark:hover:bg-green-900/20'
            >
              {exportZohoMutation.isLoading ? (
                <div className='flex flex-col items-center'>
                  <LoadingSpinner />
                  <h4 className='text-lg font-medium text-gray-900 dark:text-gray-200 mt-3'>
                    Exporting to Zoho CRM...
                  </h4>
                  <p className='text-sm text-gray-500 mt-1 text-center dark:text-gray-200'>
                    This may take 30-60 seconds. Please wait...
                  </p>
                </div>
              ) : (
                <>
                  <ArrowDownTrayIcon className='h-12 w-12 text-gray-600 mb-3 dark:text-gray-200' />
                  <h4 className='text-lg font-medium text-gray-900 dark:text-gray-200'>
                    Export to Zoho CRM
                  </h4>
                  <p className='text-sm text-gray-500 mt-1 text-center dark:text-gray-200'>
                    {isZohoConnected
                      ? 'Export stores as leads with automatic de-duplication'
                      : 'Configure and connect Zoho CRM to enable export'}
                  </p>
                </>
              )}
            </button>
          </div>

          {/* Info Box */}
          <div className='mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800'>
            <h4 className='text-sm font-medium text-blue-900 dark:text-blue-300 mb-2'>
              🔄 Automatic Processing
            </h4>
            <ul className='text-sm text-blue-800 dark:text-blue-300 space-y-1'>
              <li>
                ✓ De-duplicates against{' '}
                {accountsStatus?.data?.has_accounts
                  ? 'uploaded local accounts'
                  : 'existing Zoho Accounts'}{' '}
                by phone number
              </li>
              <li>✓ Maps Company Name to Last Name automatically</li>
              <li>✓ Adds State-Date tags (e.g., MS-101425)</li>
              <li>✓ Removes duplicate leads during import</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Export History */}
      <div className='card'>
        <div className='card-header'>
          <h3 className='text-lg font-medium text-gray-900 dark:text-white'>
            Export History
          </h3>
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
            Recent export operations with timestamps and summaries
          </p>
        </div>
        <div className='card-body'>
          {/* State Filter */}
          <div className='mb-4'>
            <div className='flex items-center space-x-3'>
              <label className='text-sm font-medium text-gray-700 dark:text-gray-300'>
                Filter by State:
              </label>
              <select
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
                className='input text-sm'
              >
                <option value=''>All States</option>
                <option value='AL'>Alabama</option>
                <option value='AK'>Alaska</option>
                <option value='AZ'>Arizona</option>
                <option value='AR'>Arkansas</option>
                <option value='CA'>California</option>
                <option value='CO'>Colorado</option>
                <option value='CT'>Connecticut</option>
                <option value='DE'>Delaware</option>
                <option value='FL'>Florida</option>
                <option value='GA'>Georgia</option>
                <option value='HI'>Hawaii</option>
                <option value='ID'>Idaho</option>
                <option value='IL'>Illinois</option>
                <option value='IN'>Indiana</option>
                <option value='IA'>Iowa</option>
                <option value='KS'>Kansas</option>
                <option value='KY'>Kentucky</option>
                <option value='LA'>Louisiana</option>
                <option value='ME'>Maine</option>
                <option value='MD'>Maryland</option>
                <option value='MA'>Massachusetts</option>
                <option value='MI'>Michigan</option>
                <option value='MN'>Minnesota</option>
                <option value='MS'>Mississippi</option>
                <option value='MO'>Missouri</option>
                <option value='MT'>Montana</option>
                <option value='NE'>Nebraska</option>
                <option value='NV'>Nevada</option>
                <option value='NH'>New Hampshire</option>
                <option value='NJ'>New Jersey</option>
                <option value='NM'>New Mexico</option>
                <option value='NY'>New York</option>
                <option value='NC'>North Carolina</option>
                <option value='ND'>North Dakota</option>
                <option value='OH'>Ohio</option>
                <option value='OK'>Oklahoma</option>
                <option value='OR'>Oregon</option>
                <option value='PA'>Pennsylvania</option>
                <option value='RI'>Rhode Island</option>
                <option value='SC'>South Carolina</option>
                <option value='SD'>South Dakota</option>
                <option value='TN'>Tennessee</option>
                <option value='TX'>Texas</option>
                <option value='UT'>Utah</option>
                <option value='VT'>Vermont</option>
                <option value='VA'>Virginia</option>
                <option value='WA'>Washington</option>
                <option value='WV'>West Virginia</option>
                <option value='WI'>Wisconsin</option>
                <option value='WY'>Wyoming</option>
              </select>
            </div>
          </div>
          {exportHistoryLoading ? (
            <LoadingSpinner />
          ) : exportHistory?.data?.length > 0 ? (
            <div className='space-y-3'>
              {exportHistory.data
                .filter(
                  (exportJob: any) =>
                    !stateFilter || exportJob.state === stateFilter
                )
                .map((exportJob: any) => (
                  <div
                    key={exportJob.id}
                    className='flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600'
                  >
                    <div className='flex items-center space-x-3'>
                      <div className='flex-shrink-0'>
                        {exportJob.status === 'completed' ? (
                          <CheckCircleIcon className='h-5 w-5 text-green-500' />
                        ) : exportJob.status === 'failed' ? (
                          <XCircleIcon className='h-5 w-5 text-red-500' />
                        ) : (
                          <ExclamationTriangleIcon className='h-5 w-5 text-yellow-500' />
                        )}
                      </div>
                      <div>
                        <div className='flex items-center space-x-2'>
                          <span className='text-sm font-medium text-gray-900 dark:text-white'>
                            {exportJob.export_type.toUpperCase()} Export
                          </span>
                          {exportJob.state && (
                            <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'>
                              {exportJob.state}
                            </span>
                          )}
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              exportJob.status === 'completed'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : exportJob.status === 'failed'
                                ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                            }`}
                          >
                            {exportJob.status}
                          </span>
                        </div>
                        <div className='text-sm text-gray-500 dark:text-gray-400'>
                          {formatDateTime(exportJob.created_at)}
                        </div>
                      </div>
                    </div>
                    <div className='flex items-center space-x-3'>
                      <div className='text-right text-sm text-gray-600 dark:text-gray-300'>
                        <div>
                          {exportJob.exported_count} exported
                          {exportJob.duplicates_removed > 0 && (
                            <span className='text-yellow-600 dark:text-yellow-400'>
                              {' '}
                              ({exportJob.duplicates_removed} duplicates
                              removed)
                            </span>
                          )}
                          {exportJob.failed_count > 0 && (
                            <span className='text-red-600 dark:text-red-400'>
                              {' '}
                              ({exportJob.failed_count} failed)
                            </span>
                          )}
                        </div>
                        <div className='text-xs text-gray-500 dark:text-gray-400'>
                          {exportJob.total_stores} total stores
                        </div>
                      </div>
                      {exportJob.status === 'failed' && (
                        <button
                          onClick={() => handleDeleteExportJob(exportJob.id)}
                          disabled={deleteExportJobMutation.isLoading}
                          className='flex items-center justify-center w-8 h-8 text-red-600 hover:text-red-800 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                          title='Delete failed export job'
                        >
                          <TrashIcon className='h-4 w-4' />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <div className='text-center py-8 text-gray-500 dark:text-gray-400'>
              <ArrowDownTrayIcon className='h-12 w-12 mx-auto mb-4 text-gray-300 dark:text-gray-600' />
              <p>No export history yet</p>
              <p className='text-sm'>Export operations will appear here</p>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={cancelDeleteExportJob}
        onConfirm={confirmDeleteExportJob}
        title='Delete Failed Export Job'
        message='Are you sure you want to delete this failed export job? This action cannot be undone.'
        confirmText='Delete'
        cancelText='Cancel'
        confirmButtonClass='bg-red-600 hover:bg-red-700 focus:ring-red-500'
        isLoading={deleteExportJobMutation.isLoading}
      />
    </div>
  )
}
